import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, FreeMode } from "swiper/modules";
import s from "./slider.module.scss";

// Импорт стилей Swiper
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/free-mode";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { getFullName } from "@utils/lib/getFullname";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { AppImage, DEFAULT_AVATAR_URL } from "@shared/ui/app-image";
import classNames from "classnames";
import { CustomVideoPlayer } from "@shared/ui/custom-video-player";

export const Slider = ({ teacher, fullWidth }) => {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const navigationPrevRef = useRef(null);
  const navigationNextRef = useRef(null);

  // Сервисные фото показываем строго по флагу service_info_enabled;
  // первое сервисное фото заменяет аватарку
  const hasServicePhotos = !!teacher?.service_info_enabled && !!teacher?.service_photos?.length;
  const photoSlides = hasServicePhotos
    ? (teacher?.service_photos || []).map((el) => ({
        id: el?.id,
        image: process.env.REACT_APP_STORAGE_URL + el?.file,
      }))
    : (teacher?.photos || []).map((el) => ({
        id: el?.id,
        image: process.env.REACT_APP_STORAGE_URL + el?.file,
      }));

  if (photoSlides.length <= 0 && !teacher?.video_presentation) {
    return (
      <div className={classNames(s.customSwiperContainer, fullWidth ? s.fullWidth : '')}>
        <div className={s.mainSwiperWrapper}>
          <div className={s.mainSlide}>
            <AppImage
              src={
                hasServicePhotos
                  ? process.env.REACT_APP_STORAGE_URL + teacher?.service_photos?.[0]?.file
                  : teacher?.avatar
                    ? process.env.REACT_APP_STORAGE_URL + teacher?.avatar.image
                    : undefined
              }
              fallbackSrc={DEFAULT_AVATAR_URL}
              className={s.mainSlideImage}
              alt={getFullName(teacher)}
            />
          </div>
        </div>
      </div>
    );
  }

  const slides = [
    hasServicePhotos
      ? {
          id: teacher?.service_photos?.[0]?.id || 'service-photo',
          image:
            process.env.REACT_APP_STORAGE_URL + teacher?.service_photos?.[0]?.file,
        }
      : teacher?.avatar
        ? {
            id: "avatar",
            image: process.env.REACT_APP_STORAGE_URL + teacher?.avatar?.image,
          }
        : null,
    ...(hasServicePhotos
      ? teacher?.service_photos?.slice(1).map((el) => ({
          id: el?.id,
          image: process.env.REACT_APP_STORAGE_URL + el?.file,
        })) || []
      : photoSlides),
		teacher?.video_presentation
			? {
					id: "video",
					video:
						process.env.REACT_APP_STORAGE_URL + teacher?.video_presentation,
				}
			: null,
  ];

  return (
    <div className={classNames(s.customSwiperContainer, fullWidth ? s.fullWidth : '')}>
      {/* Основной слайдер */}
			<PhotoProvider
				maskOpacity={0.5}
				speed={() => 600}>
      <div className={s.mainSwiperWrapper}>
        <Swiper
          modules={[Thumbs, Navigation]}
          navigation={{
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          }}
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          spaceBetween={10}
          className={s.mainSwiper}
          onInit={(swiper) => {
            // Присваиваем ref элементы после инициализации
            swiper.params.navigation.prevEl = navigationPrevRef.current;
            swiper.params.navigation.nextEl = navigationNextRef.current;
            swiper.navigation.init();
            swiper.navigation.update();
          }}
        >
          {slides.map((slide) =>
            slide?.image || slide?.video ? (
              <SwiperSlide key={slide.id}>
                {slide?.video ? (
                  <PhotoView
                    key={slide.id}
                    width={1280}
                    height={720}
                    render={({ attrs }) => (
                      <div
                        className={classNames(s.videoRenderWrapper, attrs.className)}
                        style={attrs.style}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        onWheel={(e) => e.stopPropagation()}
                      >
                        <CustomVideoPlayer
                          wrapperClassName={s.videoPlayerWrapper}
                          src={slide.video}
                          autoPlay
                          controls
                          allowFullscreen
                          skipSeconds={15}
                        />
                      </div>
                    )}
                  >
                    <div className={s.mainSlide}>
                      <div className={s.playButton}>
                        <button type="button" className={s.iconWrapper} aria-label="Воспроизвести видео">
                          <IconPlayerPlay className={s.icon} />
                        </button>
                      </div>
                      <video
                        src={slide.video}
                        className={s.mainSlideImage}
                        alt={getFullName(teacher)}
                        controls={false}
                        autoPlay={false}
                      />
                    </div>
                  </PhotoView>
                ) : (
                  <div className={s.mainSlide}>
                    <PhotoView key={slide.id} src={slide?.image}>
                      <AppImage
                        src={slide.image}
                        className={s.mainSlideImage}
                        alt={getFullName(teacher)}
                      />
                    </PhotoView>
                  </div>
                )}
              </SwiperSlide>
            ) : null
          )}
          <div className={s.navButtons}>
            <button ref={navigationPrevRef} className={s.customNavButton}>
              <IconChevronLeft />
            </button>
            <button ref={navigationNextRef} className={s.customNavButton}>
              <IconChevronRight />
            </button>
          </div>
        </Swiper>
      </div>
			</PhotoProvider>

      {/* Thumbnail слайдер */}
      <div className={s.thumbsSwiperWrapper}>
        <Swiper
          modules={[Thumbs, FreeMode]}
          onSwiper={setThumbsSwiper}
          freeMode={true}
          className={s.thumbsSwiper}
          wrapperClass={s.thumbSwiperWrapper}
        >
          {slides.map((slide) =>
            slide?.image || slide?.video ? (
              <SwiperSlide key={slide.id} className={s.thumbSlideWrapper}>
                <div className={s.thumbSlide}>
                  {slide?.video ? (
                    <>
                      <div className={s.playButton}>
                        <div className={s.iconWrapper}>
                          <IconPlayerPlay className={s.icon} />
                        </div>
                      </div>
                      <video
                        src={slide?.video}
                        className={s.thumbSlideImage}
                        alt={getFullName(teacher)}
                        controls={false}
                        autoPlay={false}
                      />
                    </>
                  ) : (
                    <AppImage
                      src={slide.image}
                      className={s.thumbSlideImage}
                      alt={getFullName(teacher)}
                    />
                  )}
                </div>
              </SwiperSlide>
            ) : null
          )}
        </Swiper>
      </div>
    </div>
  );
};
